import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicRecording, RoleEnum } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { getRecordingQueryKey } from "../api/get-recording";
import { useUpdateRecording } from "../api/update-recording";
import {
  UpdateRecordingFormData,
  updateRecordingSchema,
} from "../types/update-recording-schema";

type RecordingTitleProps = {
  isEditing: boolean;
  recording: PublicRecording;
  setIsEditing: (isEditing: boolean) => void;
};

export function RecordingTitle(props: RecordingTitleProps) {
  const { isEditing, setIsEditing, recording } = props;
  const { organisation, hasPermission } = useOrganisation();
  const isOwner = recording.organisation.id === organisation.id;
  const { mutateAsync: updateRecording } = useUpdateRecording({
    onSuccess: (_, { data }) => {
      setIsEditing(false);
      queryClient.setQueryData(
        getRecordingQueryKey(organisation.id, recording.id),
        (oldData: PublicRecording) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            name: data.name,
          };
        },
      );
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  const form = useForm<UpdateRecordingFormData>({
    resolver: zodResolver(updateRecordingSchema),
    defaultValues: {
      name: recording.name,
    },
  });

  useEffect(() => {
    form.reset({
      name: recording.name,
    });
  }, [recording.name, form, isEditing]);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!isEditing) return;

    function handleClickOutside(event: MouseEvent) {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsEditing(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isEditing, setIsEditing]);

  const handleUpdateRecording = async (data: UpdateRecordingFormData) => {
    try {
      await updateRecording({
        orgId: organisation.id,
        id: recording.id,
        data,
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  };

  if (!hasPermission(RoleEnum.CALL_MANAGEMENT) || !isOwner) {
    return (
      <h1 className="text-lg font-semibold text-foreground">
        {recording.name}
      </h1>
    );
  }

  if (isEditing) {
    return (
      <Form {...form}>
        <form ref={formRef} onSubmit={form.handleSubmit(handleUpdateRecording)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="w-64"
                    {...field}
                    disabled={form.formState.isSubmitting}
                    autoFocus
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </form>
      </Form>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <h1
            className="cursor-pointer text-lg font-semibold text-foreground"
            onClick={() => setIsEditing(true)}
          >
            {recording.name}
          </h1>
        </TooltipTrigger>
        <TooltipContent side="right">Edit Recording Name</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
